/**
 * /admin/client-reports — Admin "Client Reports" (MR Inc 4.6).
 *
 * Three numbered tabs mirroring the workflow:
 *   1 · Page-1 content     — download templates; upload the month's Word review + stats Excel;
 *                            Preview opens the page-1 PDF in a new tab; Save stores it (a
 *                            re-save Overwrites the active version, previous kept in history).
 *   2 · Batch produce      — dry-run summary + preview one sample report (full 2-pager, on the
 *                            fly, not saved), then Produce freezes a report per active agreement.
 *   3 · View produced MRs  — archive by agreement or by month, per-report download + per-month ZIP.
 *
 * PDFs open in a NEW TAB (never embedded) — the strict CSP (default-src 'self'; object-src
 * 'none') blocks blob: framing by design; a top-level tab is CSP-safe. Admin-only.
 */
import { useState } from 'react';
import {
  Alert, Anchor, Button, Container, Divider, Group, List, Loader, Modal, Paper, SegmentedControl,
  Stack, Table, Tabs, Text, Title,
} from '@mantine/core';
import { MonthPickerInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import { IconAlertTriangle, IconDownload, IconExternalLink, IconFileText, IconTable } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';

import { clientReportsApi } from '../api/clientReports';
import type { MrTemplate, MrTrackPreset } from '../api/clientReports';
import { ADMIN_PAGE_READ_ROLES, type UUID } from '../api/types';
import { AgreementPicker } from '../components/EntityPicker';
import { DocxDropzone } from '../components/DocxDropzone';
import { XlsxDropzone } from '../components/XlsxDropzone';
import { AccessDeniedAlert } from '../components/RequireAccess';
import { BackToCompliance } from '../components/BackToCompliance';
import { useCurrentUser } from '../hooks/useAuth';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

function ym(d: Date): { year: number; month: number } {
  return { year: d.getFullYear(), month: d.getMonth() + 1 };
}
function monthKey(d: Date): string {
  const { year, month } = ym(d);
  return `${year}-${String(month).padStart(2, '0')}`;
}
function ymOf(iso: string): { year: number; month: number } {
  return { year: Number(iso.slice(0, 4)), month: Number(iso.slice(5, 7)) };
}
function toast(color: string, title: string, message: string) {
  notifications.show({ color, title, message, position: 'top-center' });
}
function toastError(title: string, err: unknown) {
  toast('red', title, err instanceof Error ? err.message : 'Something went wrong.');
}
function openBlobInNewTab(blob: Blob) {
  window.open(URL.createObjectURL(blob), '_blank');
}

export function ClientReportsPage() {
  useDocumentTitle('Client Reports');
  const { user } = useCurrentUser();

  const [tab, setTab] = useState<string>('publish');

  const monthsQuery = useQuery({
    queryKey: ['client-reports', 'months'],
    queryFn: () => clientReportsApi.listMonths(),
    enabled: user?.role === 'admin',
  });

  // Publish flow.
  const [pubMonth, setPubMonth] = useState<Date | null>(null);
  const [wordFile, setWordFile] = useState<File | null>(null);
  const [statsFile, setStatsFile] = useState<File | null>(null);
  const [pubBusy, setPubBusy] = useState(false);
  const [pubDone, setPubDone] = useState(false);
  const [previewLang, setPreviewLang] = useState<'pt' | 'en'>('pt');  // which side to preview (Inc 6c)

  // Produce flow.
  const [prodMonth, setProdMonth] = useState<Date | null>(null);
  const [prodWindow, setProdWindow] = useState<MrTrackPreset>('5y');  // track-record length (default 5yrs)
  const [prodSince, setProdSince] = useState<Date | null>(null);
  const [prodTemplate, setProdTemplate] = useState<MrTemplate>('A');   // MR layout (A current / B new / C = B + portfolio pages)
  const [prodBusy, setProdBusy] = useState(false);
  const [prodDone, setProdDone] = useState<{ produced: number; overwritten: number; skipped: number } | null>(null);

  // Archive.
  const [arcBy, setArcBy] = useState<'agreement' | 'month'>('agreement');
  const [arcAgr, setArcAgr] = useState<UUID | null>(null);
  const [arcMonth, setArcMonth] = useState<Date | null>(null);

  const monthsOnFile = monthsQuery.data ?? [];
  const monthKeys = new Set(monthsOnFile.map((m) => m.period_month.slice(0, 7)));
  const pubWouldOverwrite = pubMonth ? monthKeys.has(monthKey(pubMonth)) : false;
  const prodHasPage1 = prodMonth ? monthKeys.has(monthKey(prodMonth)) : false;

  const dryRun = useQuery({
    // The template is part of the dry run: C names the agreements left without a portfolio page (MC6).
    queryKey: ['client-reports', 'dry-run', prodMonth ? monthKey(prodMonth) : '', prodTemplate],
    queryFn: () => {
      const { year, month } = ym(prodMonth as Date);
      return clientReportsApi.produce(year, month, 'preview', '5y', null, prodTemplate);
    },
    enabled: !!prodMonth && prodHasPage1,
  });

  const arcAgrQuery = useQuery({
    queryKey: ['client-reports', 'by-agreement', arcAgr],
    queryFn: () => clientReportsApi.listAgreementReports(arcAgr as UUID),
    enabled: !!arcAgr,
  });
  const arcMonthQuery = useQuery({
    queryKey: ['client-reports', 'by-month', arcMonth ? monthKey(arcMonth) : ''],
    queryFn: () => { const { year, month } = ym(arcMonth as Date); return clientReportsApi.listProduced(year, month); },
    enabled: !!arcMonth,
  });

  if (!user) return null;
  if (!ADMIN_PAGE_READ_ROLES.includes(user.role)) return <AccessDeniedAlert />;
  const isAdmin = user.role === 'admin';

  const canPreview = !!pubMonth && !!wordFile && !!statsFile;

  async function handleDownloadTemplate(kind: 'word' | 'stats') {
    try {
      await (kind === 'word' ? clientReportsApi.downloadPage1Template() : clientReportsApi.downloadStatsTemplate());
    } catch (err) { toastError('Download failed', err); }
  }

  async function handlePreview() {
    if (!pubMonth || !wordFile || !statsFile) return;
    setPubBusy(true);
    try {
      const { year, month } = ym(pubMonth);
      openBlobInNewTab(await clientReportsApi.previewPdf(year, month, wordFile, statsFile, previewLang));
    } catch (err) { toastError('Preview failed', err); } finally { setPubBusy(false); }
  }

  async function handleSave() {
    if (!pubMonth || !wordFile || !statsFile) return;
    setPubBusy(true);
    try {
      const { year, month } = ym(pubMonth);
      await clientReportsApi.uploadPage1(year, month, 'commit', wordFile, statsFile);
      setWordFile(null); setStatsFile(null);
      setPubDone(true);
      void monthsQuery.refetch();
    } catch (err) { toastError('Save failed', err); } finally { setPubBusy(false); }
  }

  async function viewOnFile(iso: string) {
    try {
      const { year, month } = ymOf(iso);
      openBlobInNewTab(await clientReportsApi.page1Pdf(year, month, previewLang));
    } catch (err) { toastError('Could not open the page-1 PDF', err); }
  }

  const prodTrackSince = prodWindow === 'since' && prodSince ? monthKey(prodSince) : null;
  const prodTrackReady = prodWindow !== 'since' || !!prodSince;  // 'Since' needs a month

  function previewSample() {
    const first = dryRun.data?.produced[0];
    if (!first || !prodMonth) return;
    const { year, month } = ym(prodMonth);
    const q = `?year=${year}&month=${month}&track_preset=${prodWindow}&template=${prodTemplate}`
      + (prodTrackSince ? `&track_since=${prodTrackSince}` : '');
    window.open(`/agreements/${first.agreement_id}/monthly-report.pdf${q}`, '_blank');
  }

  async function handleProduce() {
    if (!prodMonth) return;
    setProdBusy(true);
    try {
      const { year, month } = ym(prodMonth);
      const res = await clientReportsApi.produce(year, month, 'commit', prodWindow, prodTrackSince, prodTemplate);
      setProdDone({ produced: res.produced.length, overwritten: res.overwritten.length, skipped: res.skipped_no_data.length });
      void dryRun.refetch();
    } catch (err) { toastError('Produce failed', err); } finally { setProdBusy(false); }
  }

  async function downloadRow(agreementId: UUID, iso: string) {
    try { const { year, month } = ymOf(iso); await clientReportsApi.downloadRecorded(agreementId, year, month); }
    catch (err) { toastError('Download failed', err); }
  }
  async function downloadZip() {
    if (!arcMonth) return;
    try { const { year, month } = ym(arcMonth); await clientReportsApi.downloadProducedZip(year, month); }
    catch (err) { toastError('Download failed', err); }
  }

  const overwriteCount = dryRun.data?.overwritten.length ?? 0;

  return (
    <Container size="xl">
      {/* Success modals (mid-screen hand-offs). */}
      <Modal opened={pubDone} onClose={() => setPubDone(false)} title="Page 1 saved" centered>
        <Stack>
          <Text size="sm">The page-1 content for this month is on file. Next, produce the full Monthly Reports for the month.</Text>
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setPubDone(false)}>Stay here</Button>
            <Button onClick={() => { setPubDone(false); setTab('produce'); }}>Continue to Batch produce</Button>
          </Group>
        </Stack>
      </Modal>
      <Modal opened={!!prodDone} onClose={() => setProdDone(null)} title="Reports produced" centered>
        <Stack>
          <Text size="sm">
            {prodDone?.produced} report(s) produced
            {prodDone?.overwritten ? ` · ${prodDone.overwritten} replaced` : ''}
            {prodDone?.skipped ? ` · ${prodDone.skipped} skipped (no data)` : ''}.
          </Text>
          <Text size="sm" c="dimmed">Each report is now on record — view it here in the archive, or on each Agreement&apos;s Documents tab.</Text>
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setProdDone(null)}>Stay here</Button>
            <Button onClick={() => { setProdDone(null); setTab('archive'); }}>View in Archive</Button>
          </Group>
        </Stack>
      </Modal>

      <Stack>
        {isAdmin
          ? <BackToCompliance to="/admin" label="Back to Admin" />
          : <BackToCompliance to="/" label="Back to Welcome" />}
        <Title order={2}>Client Reports</Title>
        <Text size="sm" c="dimmed">
          {isAdmin
            ? <>Upload each month&apos;s generic page-1 content, produce the per-agreement Monthly
              Reports, and view the archive. Users retrieve produced reports from each
              Agreement&apos;s Documents tab.</>
            : <>The Monthly Reports produced so far, by agreement or by month. Each is also on
              its Agreement&apos;s Documents tab.</>}
        </Text>

        {/* P45 (Pedro 2026-10-15): RM and Compliance see only "View produced MRs" (single
            reports and the month ZIP); uploading and producing are Admin's. */}
        <Tabs value={isAdmin ? tab : 'archive'} onChange={(v) => setTab(v ?? 'publish')}>
          <Tabs.List>
            {isAdmin && <>
              <Tabs.Tab value="publish">1 · Page-1 content</Tabs.Tab>
              <Tabs.Tab value="produce">2 · Batch produce full MR</Tabs.Tab>
            </>}
            <Tabs.Tab value="archive">{isAdmin ? '3 · View produced MRs' : 'View produced MRs'}</Tabs.Tab>
          </Tabs.List>

          {isAdmin && <>
          {/* 1 · PAGE-1 CONTENT */}
          <Tabs.Panel value="publish" pt="md">
            <Stack>
              <Paper withBorder p="md">
                <Stack gap="xs">
                  <Title order={5}>Download a template</Title>
                  <Text size="sm" c="dimmed">
                    No template yet? Download the Word review + stats Excel, fill them offline,
                    then upload them below. The Word template has two sections separated by a page
                    break — Portuguese (page 1) and English (page 2); fill both and keep the page
                    break (it splits the free text into the PT and EN versions).
                  </Text>
                  <Group>
                    <Button variant="light" leftSection={<IconFileText size={16} />} onClick={() => handleDownloadTemplate('word')}>Word review template</Button>
                    <Button variant="light" leftSection={<IconTable size={16} />} onClick={() => handleDownloadTemplate('stats')}>Stats Excel template</Button>
                  </Group>
                </Stack>
              </Paper>

              <Paper withBorder p="md">
                <Stack>
                  <Title order={5}>Upload page-1 content</Title>
                  <MonthPickerInput
                    label="Reporting month" placeholder="Pick the month"
                    value={pubMonth} onChange={setPubMonth} maw={240}
                  />
                  <Group grow align="flex-start">
                    <div>
                      <Text size="sm" fw={500} mb={4}>Word review</Text>
                      <DocxDropzone onSelect={setWordFile} onReject={() => toast('red', 'Not accepted', 'Choose one .docx file.')} />
                      {wordFile && <Text size="xs" c="dimmed" mt={4}>{wordFile.name}</Text>}
                    </div>
                    <div>
                      <Text size="sm" fw={500} mb={4}>Stats Excel</Text>
                      <XlsxDropzone hint="One Excel file: the month's statistics workbook." onSelect={setStatsFile} onReject={() => toast('red', 'Not accepted', 'Choose one .xlsx file.')} />
                      {statsFile && <Text size="xs" c="dimmed" mt={4}>{statsFile.name}</Text>}
                    </div>
                  </Group>
                  <Group>
                    <SegmentedControl
                      size="xs"
                      value={previewLang}
                      onChange={(v) => setPreviewLang(v as 'pt' | 'en')}
                      data={[{ label: 'PT', value: 'pt' }, { label: 'EN', value: 'en' }]}
                    />
                    <Button variant="light" leftSection={<IconExternalLink size={16} />} onClick={handlePreview} loading={pubBusy} disabled={!canPreview}>
                      Preview page 1 ({previewLang.toUpperCase()})
                    </Button>
                    <Button
                      color={pubWouldOverwrite ? 'orange' : undefined}
                      onClick={handleSave} loading={pubBusy} disabled={!canPreview}
                    >
                      {pubWouldOverwrite ? 'Overwrite' : 'Save'}
                    </Button>
                  </Group>
                  {pubWouldOverwrite && canPreview && (
                    <Text size="xs" c="orange.7">
                      This month already has page-1 content — saving replaces the active version
                      (the previous one is kept in history).
                    </Text>
                  )}
                </Stack>
              </Paper>

              <Paper withBorder p="md">
                <Stack gap="xs">
                  <Title order={5}>Page-1 content on file</Title>
                  {monthsOnFile.length === 0 ? (
                    <Text size="sm" c="dimmed">No page-1 content uploaded yet.</Text>
                  ) : (
                    <Table striped highlightOnHover>
                      <Table.Thead>
                        <Table.Tr><Table.Th>Month</Table.Th><Table.Th>Word</Table.Th><Table.Th>Excel</Table.Th><Table.Th>Saved</Table.Th><Table.Th /></Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {monthsOnFile.map((m) => (
                          <Table.Tr key={m.id}>
                            <Table.Td>{m.period_month.slice(0, 7)}</Table.Td>
                            <Table.Td>{m.word_filename}</Table.Td>
                            <Table.Td>{m.xlsx_filename}</Table.Td>
                            <Table.Td>{m.created_at.slice(0, 10)}</Table.Td>
                            <Table.Td><Anchor size="sm" onClick={() => viewOnFile(m.period_month)}>View</Anchor></Table.Td>
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                    </Table>
                  )}
                </Stack>
              </Paper>
            </Stack>
          </Tabs.Panel>

          {/* 2 · BATCH PRODUCE */}
          <Tabs.Panel value="produce" pt="md">
            <Paper withBorder p="md">
              <Stack>
                <Title order={5}>Batch produce full Monthly Reports</Title>
                <Text size="sm" c="dimmed">Freezes a report for every active agreement with data. Requires page-1 content for the month.</Text>
                <MonthPickerInput label="Reporting month" placeholder="Pick the month" value={prodMonth} onChange={setProdMonth} maw={240} />

                <Group align="flex-start" gap="xl" wrap="wrap">
                  <Stack gap={4}>
                    <Text size="sm" fw={500}>Track record shown</Text>
                    <Group align="flex-end" gap="md">
                      <SegmentedControl
                        size="xs"
                        value={prodWindow}
                        onChange={(v) => setProdWindow(v as MrTrackPreset)}
                        data={[
                          { label: 'Last 12m', value: '12m' },
                          { label: 'Last 3yrs', value: '3y' },
                          { label: 'Last 5yrs', value: '5y' },
                          { label: 'Max', value: 'max' },
                          { label: 'Since…', value: 'since' },
                        ]}
                      />
                      {prodWindow === 'since' && (
                        <MonthPickerInput size="xs" placeholder="Start month" value={prodSince} onChange={setProdSince} maw={170} />
                      )}
                    </Group>
                    <Text size="xs" c="dimmed">
                      How much history each report&apos;s return graph + table show, per agreement
                      (shorter when the client&apos;s own history is shorter).
                    </Text>
                  </Stack>

                  <Stack gap={4}>
                    <Text size="sm" fw={500}>Template</Text>
                    <SegmentedControl
                      size="xs"
                      value={prodTemplate}
                      onChange={(v) => setProdTemplate(v as MrTemplate)}
                      data={[
                        { label: 'A (current)', value: 'A' },
                        { label: 'B (new)', value: 'B' },
                        { label: 'C (B + portfolio)', value: 'C' },
                      ]}
                    />
                    <Text size="xs" c="dimmed" maw={300}>
                      A = current page 2. B = since-inception return graph + a returns-by-period
                      table (YTD · 3yr · 5yr · 10yr · SI); the matrix still follows the window.
                      C = B + the agreement&apos;s portfolios at the month&apos;s last positions, on
                      landscape pages, in its Preferred Ref Ccy.
                    </Text>
                  </Stack>
                </Group>

                {prodMonth && !prodHasPage1 && (
                  <Alert color="orange" variant="light" icon={<IconAlertTriangle size={16} />}>
                    No page-1 content for this month yet. Upload it on the “1 · Page-1 content” tab first.
                  </Alert>
                )}

                {prodMonth && prodHasPage1 && dryRun.isLoading && (
                  <Group gap="xs"><Loader size="sm" /><Text size="sm" c="dimmed">Retrieving data…</Text></Group>
                )}

                {prodMonth && prodHasPage1 && dryRun.data && (
                  <Stack gap="xs">
                    <Text size="sm">
                      <b>{dryRun.data.produced.length}</b> to produce
                      {overwriteCount > 0 && <> · <b>{overwriteCount}</b> will be replaced</>}
                      {dryRun.data.skipped_no_data.length > 0 && <> · <b>{dryRun.data.skipped_no_data.length}</b> skipped (no data)</>}
                    </Text>
                    {dryRun.data.skipped_no_data.length > 0 && (
                      <Text size="xs" c="dimmed">Skipped: {dryRun.data.skipped_no_data.map((a) => a.mnemonic ?? a.code).join(', ')}</Text>
                    )}
                    {prodTemplate === 'C' && (dryRun.data.no_portfolio_page ?? []).length > 0 && (
                      <Alert color="orange" variant="light" icon={<IconAlertTriangle size={16} />}
                        title={`${dryRun.data.no_portfolio_page.length} without a portfolio page (produced as Template B)`}>
                        {dryRun.data.no_portfolio_page.map((a) => `${a.code ?? ''} ${a.mnemonic ?? ''}: ${a.reason ?? ''}`).join(' · ')}
                      </Alert>
                    )}
                    <Group>
                      <Button variant="light" leftSection={<IconExternalLink size={16} />} onClick={previewSample} disabled={!dryRun.data.produced.length || !prodTrackReady}>
                        Preview a sample report
                      </Button>
                      <Button color={overwriteCount > 0 ? 'orange' : undefined} onClick={handleProduce} loading={prodBusy} disabled={!dryRun.data.produced.length || !prodTrackReady}>
                        {overwriteCount > 0 ? 'Overwrite & produce all' : 'Produce all'}
                      </Button>
                    </Group>
                    {!prodTrackReady && <Text size="xs" c="orange.7">Pick a start month for “Since…”.</Text>}
                    <Text size="xs" c="dimmed">Preview opens the first agreement in the batch as a full 2-page report (not saved).</Text>
                  </Stack>
                )}
              </Stack>
            </Paper>
          </Tabs.Panel>
          </>}

          {/* 3 · VIEW PRODUCED MRs */}
          <Tabs.Panel value="archive" pt="md">
            <Paper withBorder p="md">
              <Stack>
                <Group justify="space-between">
                  <Title order={5}>Produced reports</Title>
                  <SegmentedControl
                    value={arcBy}
                    onChange={(v) => setArcBy(v as 'agreement' | 'month')}
                    data={[{ label: 'By agreement', value: 'agreement' }, { label: 'By month', value: 'month' }]}
                  />
                </Group>

                {arcBy === 'agreement' ? (
                  <Stack>
                    <AgreementPicker label="Agreement" value={arcAgr} onChange={setArcAgr} />
                    {arcAgr && (arcAgrQuery.data ?? []).length === 0 && !arcAgrQuery.isLoading && (
                      <Text size="sm" c="dimmed">No reports on record for this agreement.</Text>
                    )}
                    {arcAgr && (arcAgrQuery.data ?? []).length > 0 && (
                      <Table striped highlightOnHover>
                        <Table.Thead><Table.Tr><Table.Th>Month</Table.Th><Table.Th>File</Table.Th><Table.Th>Produced</Table.Th><Table.Th /></Table.Tr></Table.Thead>
                        <Table.Tbody>
                          {(arcAgrQuery.data ?? []).map((r) => (
                            <Table.Tr key={r.period_month}>
                              <Table.Td>{r.period_month.slice(0, 7)}</Table.Td>
                              <Table.Td>{r.pdf_filename}</Table.Td>
                              <Table.Td>{r.created_at.slice(0, 10)}</Table.Td>
                              <Table.Td><Button size="xs" variant="light" leftSection={<IconDownload size={14} />} onClick={() => downloadRow(arcAgr, r.period_month)}>Download</Button></Table.Td>
                            </Table.Tr>
                          ))}
                        </Table.Tbody>
                      </Table>
                    )}
                  </Stack>
                ) : (
                  <Stack>
                    <Group align="flex-end">
                      <MonthPickerInput label="Month" placeholder="Pick a month" value={arcMonth} onChange={setArcMonth} maw={240} />
                      {arcMonth && (arcMonthQuery.data ?? []).length > 0 && (
                        <Button variant="light" leftSection={<IconDownload size={16} />} onClick={downloadZip}>Download all (ZIP)</Button>
                      )}
                    </Group>
                    {arcMonth && (arcMonthQuery.data ?? []).length === 0 && !arcMonthQuery.isLoading && (
                      <Text size="sm" c="dimmed">No reports produced for this month.</Text>
                    )}
                    {arcMonth && (arcMonthQuery.data ?? []).length > 0 && (
                      <Table striped highlightOnHover>
                        <Table.Thead><Table.Tr><Table.Th>Agreement</Table.Th><Table.Th>Mnemonic</Table.Th><Table.Th>File</Table.Th><Table.Th /></Table.Tr></Table.Thead>
                        <Table.Tbody>
                          {(arcMonthQuery.data ?? []).map((r) => (
                            <Table.Tr key={r.agreement_id}>
                              <Table.Td>{r.code ?? '—'}</Table.Td>
                              <Table.Td>{r.mnemonic ?? '—'}</Table.Td>
                              <Table.Td>{r.pdf_filename}</Table.Td>
                              <Table.Td><Button size="xs" variant="light" leftSection={<IconDownload size={14} />} onClick={() => downloadRow(r.agreement_id, monthKey(arcMonth) + '-01')}>Download</Button></Table.Td>
                            </Table.Tr>
                          ))}
                        </Table.Tbody>
                      </Table>
                    )}
                  </Stack>
                )}
              </Stack>
            </Paper>
          </Tabs.Panel>
        </Tabs>

        {isAdmin && <>
          <Divider />
          <List size="xs" c="dimmed">
            <List.Item>Page 1 is generic and firm-wide; the same content is used for every agreement that month.</List.Item>
            <List.Item>Producing overwrites the active report per agreement; previous versions are retained.</List.Item>
          </List>
        </>}
      </Stack>
    </Container>
  );
}
